import type { User } from "@supabase/supabase-js";

import { supabase } from "@/integrations/supabase/client";
import { setRememberSessionPreference } from "@/lib/authPersistence";
import { ensureProfile, getOnboardingCompletion } from "@/lib/onboardingProfile";

export type SocialProvider = "google" | "apple";
export type SocialAuthIntent = "signin" | "signup";

interface BeginWebSocialAuthOptions {
  provider: SocialProvider;
  intent: SocialAuthIntent;
  rememberSession: boolean;
  fallbackDisplayName?: string | null;
}

interface StoredSocialAuthContext {
  provider: SocialProvider;
  intent: SocialAuthIntent;
  fallbackDisplayName: string | null;
}

const SOCIAL_AUTH_CONTEXT_KEY = "kindred.social-auth-context";

const canUseBrowserStorage = () => typeof window !== "undefined";

const getContextStores = () => {
  if (!canUseBrowserStorage()) {
    return [];
  }

  return [window.sessionStorage, window.localStorage];
};

const clearStoredSocialAuthContext = () => {
  for (const store of getContextStores()) {
    store.removeItem(SOCIAL_AUTH_CONTEXT_KEY);
  }
};

const getContextStore = (rememberSession: boolean) =>
  rememberSession ? window.localStorage : window.sessionStorage;

const storeSocialAuthContext = (context: StoredSocialAuthContext, rememberSession: boolean) => {
  clearStoredSocialAuthContext();
  getContextStore(rememberSession).setItem(SOCIAL_AUTH_CONTEXT_KEY, JSON.stringify(context));
};

const looksLikeNewUser = (user: User | null) => {
  if (!user?.created_at || !user.last_sign_in_at) {
    return false;
  }

  return Math.abs(new Date(user.created_at).getTime() - new Date(user.last_sign_in_at).getTime()) < 60_000;
};

const readCallbackValue = (key: string) => {
  if (typeof window === "undefined") {
    return null;
  }

  const url = new URL(window.location.href);
  const hashParams = new URLSearchParams(url.hash.startsWith("#") ? url.hash.slice(1) : url.hash);
  return hashParams.get(key) ?? url.searchParams.get(key);
};

export const beginWebSocialAuth = async ({
  provider,
  intent,
  rememberSession,
  fallbackDisplayName,
}: BeginWebSocialAuthOptions) => {
  if (!canUseBrowserStorage()) {
    throw new Error("Social sign-in is only available in the browser.");
  }

  await setRememberSessionPreference(rememberSession);
  storeSocialAuthContext(
    {
      provider,
      intent,
      fallbackDisplayName: fallbackDisplayName?.trim() || null,
    },
    rememberSession,
  );

  const { error } = await supabase.auth.signInWithOAuth({
    provider,
    options: {
      redirectTo: `${window.location.origin}/auth`,
    },
  });

  if (error) {
    clearStoredSocialAuthContext();
    throw error;
  }
};

export const consumeWebSocialAuthContext = (): StoredSocialAuthContext | null => {
  for (const store of getContextStores()) {
    const rawValue = store.getItem(SOCIAL_AUTH_CONTEXT_KEY);
    if (!rawValue) {
      continue;
    }

    clearStoredSocialAuthContext();

    try {
      return JSON.parse(rawValue) as StoredSocialAuthContext;
    } catch (error) {
      console.error("Error parsing stored social auth context:", error);
      return null;
    }
  }

  return null;
};

export const getWebSocialAuthRedirectError = () => readCallbackValue("error_description") ?? readCallbackValue("error");

export const getWebSocialAuthOutcome = async (user: User) => {
  await ensureProfile(user);

  return {
    looksNewUser: looksLikeNewUser(user),
    needsOnboarding: !(await getOnboardingCompletion(user.id)),
  };
};
